<div class="flex justify-center px-4 py-8 pb-12 w-full max-w-[960px] flex-col gap-5" *ngIf="state$ | async as session">
  <div class="flex flex-col gap-1.5">
    <label for="question" class="font-semibold text-gray-900">Your question</label>
    <textarea
      id="question"
      #questionInput
      pInputTextarea
      [ngModel]="session.query"
      (ngModelChange)="onQueryChange($event)"
      rows="4"
      [autoResize]="true"
      placeholder="Ask anything stored in your notes..."
      (keydown.enter)="onQuestionEnter($event, session.query || '')"></textarea>
  </div>

  <div class="flex justify-between gap-2">
    <p-button
      label="Clear"
      icon="pi pi-times"
      severity="secondary"
      [disabled]="session.isLoading"
      (onClick)="clearDialog()"></p-button>
    <p-button
      label="Submit"
      icon="pi pi-send"
      [loading]="session.isLoading || false"
      [disabled]="!(session.query || '').trim() || session.isLoading"
      (onClick)="askQuestion(session.query || '')"></p-button>
  </div>

  <div class="flex flex-col gap-2 text-sm">
    <div class="font-medium text-gray-900">Mode</div>
    <div class="flex flex-wrap items-center gap-4">
      <label *ngFor="let mode of askModeOptions" class="inline-flex items-center gap-2">
        <input
          type="radio"
          name="askMode"
          class="h-4 w-4"
          [checked]="session.askScope === mode.value"
          [disabled]="!isModeAvailable(session, mode.value) || session.isLoading"
          (change)="setAskScope(mode.value)" />
        <span [class.text-gray-400]="!isModeAvailable(session, mode.value)">
          {{ mode.label }}<ng-container *ngIf="mode.value === 'filtered_notes'"> ({{ session.filteredNoteIds.length }})</ng-container>
        </span>
      </label>
    </div>
    <div class="text-xs text-gray-500" *ngIf="!canAskCurrentNote(session)">Ask current note is available only when opened from View or Edit.</div>
    <div class="text-xs text-gray-500" *ngIf="!canAskFilteredNotes(session)">Ask filtered notes is available only from Home when a filter is active.</div>

    <div *ngIf="session.askScope === 'all_notes'" class="mt-1 pt-2 border-t border-gray-100">
      <label class="inline-flex items-center gap-2 text-xs text-gray-400 cursor-pointer select-none">
        <input
          type="checkbox"
          class="h-3 w-3"
          [checked]="session.includeDebug"
          (change)="setIncludeDebug($any($event.target).checked)" />
        Show AI debug
      </label>
    </div>
  </div>

  <div class="flex flex-col gap-3">
    <div *ngIf="session.error" class="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800">
      <span class="font-semibold">Error:</span>
      {{ session.error }}
    </div>

    <div *ngIf="session.answer || session.isLoading" class="flex flex-col gap-3">
      <div>
        <label for="answer" class="font-semibold text-gray-900">Answer</label>
        <textarea
          id="answer"
          pInputTextarea
          [value]="session.answer"
          [readonly]="true"
          [autoResize]="true"
          rows="6"
          placeholder="The AI answer will appear here."
          class="w-full bg-gray-50"></textarea>
      </div>

      <!-- Referenced notes (new /api/ai/ask endpoint) -->
      <div *ngIf="session.sources && session.sources.length > 0" class="flex flex-col gap-2">
        <div
          class="flex items-center gap-2 cursor-pointer select-none py-1 transition-opacity hover:opacity-70"
          (click)="toggleSourcesCollapsed()">
          <span class="font-semibold text-gray-900">Referenced notes</span>
          <span class="text-xs text-gray-500">({{ session.sources.length }})</span>
          <span class="text-xs text-gray-500">{{ sourcesCollapsed ? '▶' : '▼' }}</span>
        </div>
        <div *ngIf="!sourcesCollapsed">
          <table class="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden text-sm">
            <thead>
              <tr class="bg-gray-50 border-b border-gray-200">
                <th class="px-3 py-2 text-left font-semibold text-gray-900 whitespace-nowrap">#</th>
                <th class="px-3 py-2 text-left font-semibold text-gray-900 whitespace-nowrap">Title</th>
                <th class="px-3 py-2 text-left font-semibold text-gray-900 whitespace-nowrap">Status</th>
                <th class="px-3 py-2 text-left font-semibold text-gray-900 whitespace-nowrap">Ref Date</th>
                <th class="px-3 py-2 text-left font-semibold text-gray-900 whitespace-nowrap">ID</th>
              </tr>
            </thead>
            <tbody>
              <tr
                *ngFor="let src of session.sources; let i = index"
                class="border-b border-gray-200 transition-colors hover:bg-gray-50 last:border-b-0">
                <td class="px-3 py-2 text-gray-900 font-semibold whitespace-nowrap">{{ i + 1 }}</td>
                <td class="px-3 py-2 text-gray-900 font-medium break-words">
                  <a *ngIf="src.note_id; else srcTitleText"
                     [routerLink]="['/view', src.note_id]"
                     target="_blank"
                     rel="noopener"
                     class="hover:underline cursor-pointer">{{ src.title || '(Untitled)' }}</a>
                  <ng-template #srcTitleText>{{ src.title || '(Untitled)' }}</ng-template>
                </td>
                <td class="px-3 py-2 text-gray-500 whitespace-nowrap">{{ src.status || '' }}</td>
                <td class="px-3 py-2 text-gray-500 whitespace-nowrap">{{ formatReferenceDate(src.reference_date || src.date) }}</td>
                <td class="px-3 py-2 text-gray-500 font-mono text-xs whitespace-nowrap">{{ src.note_id || '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Referenced notes (legacy /api/notes/ask endpoint) -->
      <div class="flex flex-col gap-1.5" *ngIf="shouldShowReferencedNotes(session)">
        <div class="flex items-center gap-2 cursor-pointer select-none py-1 transition-opacity hover:opacity-70" (click)="toggleReferencedNotes()">
          <span class="font-semibold text-gray-900">Referenced notes</span>
          <span class="text-xs text-gray-500">{{ referencedNotesCollapsed ? '▶' : '▼' }}</span>
        </div>
        <div class="flex flex-col gap-1.5" *ngIf="!referencedNotesCollapsed">
          <ng-container *ngIf="session.usedNoteIds.length; else noNotes">
            <table class="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden text-sm">
              <thead>
                <tr class="bg-gray-50 border-b border-gray-200">
                  <th class="px-3 py-2 text-left font-semibold text-gray-900 whitespace-nowrap">#</th>
                  <th class="px-3 py-2 text-left font-semibold text-gray-900 whitespace-nowrap">ID</th>
                  <th class="px-3 py-2 text-left font-semibold text-gray-900 whitespace-nowrap">Title</th>
                  <th class="px-3 py-2 text-left font-semibold text-gray-900 whitespace-nowrap">Status</th>
                  <th class="px-3 py-2 text-left font-semibold text-gray-900 whitespace-nowrap">Reference Date</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  class="border-b border-gray-200 transition-colors hover:bg-gray-50 last:border-b-0"
                  *ngFor="let noteId of getUniqueNoteIds(session.usedNoteIds); let i = index">
                  <td class="px-3 py-2 text-gray-900 font-semibold whitespace-nowrap">{{ i + 1 }}</td>
                  <td class="px-3 py-2 text-gray-900 font-mono text-xs max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">{{ noteId }}</td>
                  <td class="px-3 py-2 text-gray-900 font-medium break-words">
                    <a [routerLink]="['/view', noteId]"
                       target="_blank"
                       rel="noopener"
                       class="hover:underline cursor-pointer">{{ getNoteDetails(noteId)?.Title || 'Loading...' }}</a>
                  </td>
                  <td class="px-3 py-2 text-gray-500 whitespace-nowrap">
                    {{ getNoteDetails(noteId)?.Status || '' }}
                  </td>
                  <td class="px-3 py-2 text-gray-500 whitespace-nowrap">
                    {{ formatReferenceDate(getNoteDetails(noteId)?.ReferenceDate || getNoteDetails(noteId)?.created_on) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </ng-container>
          <ng-template #noNotes>
            <span class="text-gray-500 text-sm">No referenced notes returned.</span>
          </ng-template>
        </div>
      </div>

      <!-- AI debug panel (only when debug is enabled and data present) -->
      <p-panel
        *ngIf="session.includeDebug && session.debugInfo"
        header="AI Debug"
        [toggleable]="true"
        [collapsed]="true">
        <div class="flex flex-col gap-3 text-sm">
          <div class="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
            <span class="text-gray-500">Model</span>
            <span class="font-mono text-xs self-center">{{ session.debugInfo.model || '—' }}</span>
            <span class="text-gray-500">Tool calls</span>
            <span>{{ session.debugInfo.tool_call_count ?? 0 }}</span>
            <ng-container *ngIf="session.debugInfo.latency_ms != null">
              <span class="text-gray-500">Latency</span>
              <span>{{ session.debugInfo.latency_ms }}ms</span>
            </ng-container>
            <ng-container *ngIf="session.debugInfo.prompt_tokens != null">
              <span class="text-gray-500">Prompt tokens</span>
              <span>{{ session.debugInfo.prompt_tokens }}</span>
            </ng-container>
            <ng-container *ngIf="session.debugInfo.completion_tokens != null">
              <span class="text-gray-500">Completion tokens</span>
              <span>{{ session.debugInfo.completion_tokens }}</span>
            </ng-container>
            <ng-container *ngIf="session.debugInfo.total_tokens != null">
              <span class="text-gray-500">Total tokens</span>
              <span>{{ session.debugInfo.total_tokens }}</span>
            </ng-container>
            <ng-container *ngIf="session.debugInfo.tokens_used != null && session.debugInfo.total_tokens == null">
              <span class="text-gray-500">Tokens used</span>
              <span>{{ session.debugInfo.tokens_used }}</span>
            </ng-container>
          </div>

          <ng-container *ngIf="session.requestId || session.debugInfo.request_id">
            <div class="flex flex-col gap-0.5">
              <span class="text-gray-500 text-xs font-medium">Request ID</span>
              <span class="font-mono text-xs text-gray-700 break-all select-all">{{ session.requestId || session.debugInfo.request_id }}</span>
            </div>
          </ng-container>

          <div *ngIf="session.debugInfo.token_usage && session.debugInfo.total_tokens == null">
            <div class="text-gray-500 font-medium mb-1">Token usage</div>
            <pre class="text-xs bg-gray-50 rounded p-2 overflow-auto max-h-24 whitespace-pre-wrap break-all">{{ session.debugInfo.token_usage | json }}</pre>
          </div>

          <div *ngIf="session.debugInfo.executed_tools && session.debugInfo.executed_tools.length > 0">
            <div class="text-gray-500 font-medium mb-2">Executed tools</div>
            <div
              *ngFor="let tool of session.debugInfo.executed_tools"
              class="border-l-2 border-gray-200 pl-3 mb-3">
              <div class="font-mono text-xs font-semibold text-gray-800">{{ tool.tool_name }}</div>
              <div class="flex gap-4 text-xs text-gray-500 mt-0.5">
                <span *ngIf="tool.result_count != null">{{ tool.result_count }} results</span>
                <span *ngIf="tool.latency_ms != null">{{ tool.latency_ms }}ms</span>
              </div>
              <pre *ngIf="tool.arguments" class="text-xs bg-gray-50 rounded p-1.5 mt-1.5 overflow-auto max-h-20 whitespace-pre-wrap break-all">{{ tool.arguments | json }}</pre>
            </div>
          </div>

          <details>
            <summary class="cursor-pointer text-xs text-gray-400 font-medium hover:text-gray-600 select-none">Raw debug JSON</summary>
            <pre class="mt-2 text-xs bg-gray-50 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap break-all">{{ session.debugInfo | json }}</pre>
          </details>
        </div>
      </p-panel>
    </div>
  </div>
</div>
